/**
 * MHARHER - Unified Feed Page
 * TikTok-style vertical scroll with Videos, Text Posts, and Polls
 * ADN MHARHER: Sidi Bou Said colors, Tunisian culture, BARKA economy
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Heart, MessageCircle, Share2, Bookmark, Music2,
  Plus, ChefHat, Volume2, VolumeX, Play, Pause,
  Flame, MapPin, Eye, Send, X, ChevronUp,
  ChevronDown, Sparkles, Users, MoreHorizontal,
  BarChart3, Clock, CheckCircle2, Type, Vote, Radio
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

// Background gradients for text posts - Tunisian themed
const BACKGROUNDS = {
  bougainvillier: 'linear-gradient(135deg, #D946EF 0%, #F97316 100%)',
  sidi_bousaid: 'linear-gradient(135deg, #1E4ED8 0%, #60A5FA 100%)',
  harissa: 'linear-gradient(135deg, #D9381E 0%, #F2A900 100%)',
  safran: 'linear-gradient(135deg, #F2A900 0%, #FBBF24 100%)',
  olive: 'linear-gradient(135deg, #4D7C0F 0%, #84CC16 100%)',
  desert: 'linear-gradient(135deg, #D4A574 0%, #F5E6D3 100%)',
  mediterranean: 'linear-gradient(135deg, #0D3A9E 0%, #1E4ED8 50%, #3B82F6 100%)',
  sunrise_tunis: 'linear-gradient(135deg, #F97316 0%, #FB7185 50%, #A855F7 100%)',
  mosaic: 'linear-gradient(135deg, #1E4ED8 0%, #7C3AED 50%, #D946EF 100%)',
  plain_white: 'linear-gradient(135deg, #FFFFFF 0%, #F8F5F2 100%)',
  plain_dark: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
};

const TEXT_COLORS = {
  bougainvillier: 'white',
  sidi_bousaid: 'white',
  harissa: 'white',
  safran: '#1a1a1a',
  olive: 'white',
  desert: '#1a1a1a',
  mediterranean: 'white',
  sunrise_tunis: 'white',
  mosaic: 'white',
  plain_white: '#1a1a1a',
  plain_dark: 'white',
};

// Video Card Component
const VideoCard = ({ item, isActive, onLike, onSave, onShare, onFollow, onComment, isMuted, onToggleMute, t }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isActive]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  return (
    <div className="relative w-full h-full bg-black flex">
      <div className="flex-1 relative flex items-center justify-center">
        <video
          ref={videoRef}
          src={item.video_url}
          poster={item.thumbnail_url}
          className="w-full h-full object-contain max-h-[calc(100vh-3.5rem)] lg:max-h-screen"
          loop
          playsInline
          onClick={togglePlay}
          onTimeUpdate={() => {
            if (videoRef.current) {
              setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
            }
          }}
        />

        {/* Play/Pause Overlay */}
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
              onClick={togglePlay}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="w-20 h-20 rounded-full bg-white/90 shadow-2xl flex items-center justify-center"
              >
                <Play className="w-10 h-10 text-[#1E4ED8] ml-1" fill="#1E4ED8" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Sound Toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>

        {/* Recipe Link Badge */}
        {item.recipe_id && (
          <Link 
            to={`/recipe/${item.recipe_id}`}
            className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 bg-[#1E4ED8]/90 backdrop-blur-sm rounded-full text-white text-sm font-medium hover:bg-[#1E4ED8] transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <ChefHat className="w-4 h-4" />
            {t('feed.viewRecipe', 'Voir la recette')}
          </Link>
        )}

        {/* Mobile Info Overlay */}
        <div className="lg:hidden absolute bottom-0 left-0 right-16 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <CreatorInfo item={item} onFollow={onFollow} t={t} />
          <p className="text-white text-sm mb-2 line-clamp-2">{item.caption}</p>
          <HashtagList hashtags={item.hashtags} />
          {item.music_name && (
            <div className="flex items-center gap-2 text-white/70 text-sm mt-2">
              <Music2 className="w-4 h-4" />
              <span className="truncate">{item.music_name}</span>
            </div>
          )}
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden absolute right-2 bottom-24 flex flex-col items-center gap-4">
          <ActionButtons 
            item={item} 
            onLike={onLike} 
            onSave={onSave} 
            onShare={onShare} 
            onComment={onComment}
            formatCount={formatCount}
            t={t}
            vertical
          />
        </div>
      </div>

      {/* Desktop Side Panel */}
      <DesktopSidePanel 
        item={item} 
        onLike={onLike} 
        onSave={onSave} 
        onShare={onShare} 
        onFollow={onFollow}
        onComment={onComment}
        formatCount={formatCount}
        t={t}
      />
    </div>
  );
};

// Text Post Card Component
const TextPostCard = ({ item, onLike, onSave, onShare, onFollow, onComment, t }) => {
  const background = BACKGROUNDS[item.background_type] || BACKGROUNDS.sidi_bousaid;
  const textColor = TEXT_COLORS[item.background_type] || 'white';
  
  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  return (
    <div className="relative w-full h-full flex">
      {/* Main Content */}
      <div 
        className="flex-1 relative flex items-center justify-center p-8"
        style={{ background }}
      >
        {/* Decorative Pattern for Mosaic */}
        {item.background_type === 'mosaic' && (
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <pattern id="mosaic" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <polygon points="10,0 20,10 10,20 0,10" fill="white"/>
              </pattern>
              <rect width="100" height="100" fill="url(#mosaic)"/>
            </svg>
          </div>
        )}

        {/* Text Content */}
        <div className="relative z-10 max-w-2xl text-center px-6">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-2xl md:text-4xl lg:text-5xl font-bold leading-tight ${
              item.font_style === 'artistic' ? 'font-fraunces italic' : 
              item.font_style === 'bold' ? 'font-black' : 'font-semibold'
            }`}
            style={{ color: textColor }}
          >
            {item.text_content}
          </motion.p>
        </div>

        {/* Creator Info - Bottom */}
        <div className="absolute bottom-0 left-0 right-16 lg:right-96 p-4 bg-gradient-to-t from-black/50 to-transparent">
          <CreatorInfo item={item} onFollow={onFollow} t={t} light />
          <HashtagList hashtags={item.hashtags} />
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden absolute right-2 bottom-24 flex flex-col items-center gap-4">
          <ActionButtons 
            item={item} 
            onLike={onLike} 
            onSave={onSave} 
            onShare={onShare} 
            onComment={onComment}
            formatCount={formatCount}
            t={t}
            vertical
          />
        </div>
      </div>

      {/* Desktop Side Panel */}
      <DesktopSidePanel 
        item={item} 
        onLike={onLike} 
        onSave={onSave} 
        onShare={onShare} 
        onFollow={onFollow}
        onComment={onComment}
        formatCount={formatCount}
        t={t}
      />
    </div>
  );
};

// Poll Card Component
const PollCard = ({ item, onLike, onSave, onShare, onFollow, onComment, onVote, t }) => {
  const [selectedOption, setSelectedOption] = useState(item.user_voted_option);
  const [isVoting, setIsVoting] = useState(false);
  
  const totalVotes = item.poll_total_votes || 0;
  const hasVoted = !!item.user_voted_option || !!selectedOption;
  const pollEnded = item.poll_ends_at && new Date(item.poll_ends_at) < new Date();
  
  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  const getTimeRemaining = () => {
    if (!item.poll_ends_at) return null;
    const end = new Date(item.poll_ends_at);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return t('feed.pollEnded', 'Terminé');
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}j restant${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours}h restante${hours > 1 ? 's' : ''}`;
    return t('feed.endingSoon', 'Se termine bientôt');
  };

  const handleVote = async (optionId) => {
    if (hasVoted || pollEnded || isVoting) return;
    
    setIsVoting(true);
    setSelectedOption(optionId);
    
    try {
      await onVote(item.id, optionId);
    } catch (error) {
      setSelectedOption(null);
      toast.error(t('errors.voteFailed', 'Erreur lors du vote'));
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <div className="relative w-full h-full flex">
      {/* Main Content */}
      <div className="flex-1 relative flex items-center justify-center bg-gradient-to-br from-[#1E4ED8] via-[#3B82F6] to-[#60A5FA] p-6">
        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-40 left-10 w-48 h-48 rounded-full bg-[#D9381E]/10 blur-3xl" />

        {/* Poll Content */}
        <div className="relative z-10 w-full max-w-lg">
          {/* Poll Badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm px-4 py-1.5">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('feed.poll', 'Sondage')}
            </Badge>
            {getTimeRemaining() && (
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm px-4 py-1.5">
                <Clock className="w-4 h-4 mr-2" />
                {getTimeRemaining()}
              </Badge>
            )}
          </div>

          {/* Question */}
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center mb-8 px-4"
          >
            {item.poll_question}
          </motion.h2>

          {/* Options */}
          <div className="space-y-3">
            {item.poll_options?.map((option, index) => {
              const percentage = totalVotes > 0 ? Math.round((option.votes_count / totalVotes) * 100) : 0;
              const isSelected = selectedOption === option.id || item.user_voted_option === option.id;
              const showResults = hasVoted || pollEnded;
              
              return (
                <motion.button
                  key={option.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleVote(option.id)}
                  disabled={hasVoted || pollEnded || isVoting}
                  className={`
                    relative w-full p-4 rounded-2xl text-left transition-all overflow-hidden
                    ${showResults 
                      ? 'bg-white/20 backdrop-blur-sm cursor-default' 
                      : 'bg-white/10 backdrop-blur-sm hover:bg-white/25 cursor-pointer'
                    }
                    ${isSelected ? 'ring-2 ring-white' : ''}
                  `}
                >
                  {/* Progress bar background */}
                  {showResults && (
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="absolute inset-0 bg-white/20"
                    />
                  )}
                  
                  <div className="relative flex items-center justify-between">
                    <span className="flex items-center gap-3 text-white font-medium">
                      {option.emoji && <span className="text-2xl">{option.emoji}</span>}
                      {option.text}
                    </span>
                    {showResults && (
                      <div className="flex items-center gap-2">
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-white" />}
                        <span className="text-white font-bold">{percentage}%</span>
                      </div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Vote Count */}
          <p className="text-center text-white/70 mt-6 text-sm">
            {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
          </p>
        </div>

        {/* Creator Info */}
        <div className="absolute bottom-0 left-0 right-16 lg:right-96 p-4">
          <CreatorInfo item={item} onFollow={onFollow} t={t} light />
          <HashtagList hashtags={item.hashtags} />
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden absolute right-2 bottom-24 flex flex-col items-center gap-4">
          <ActionButtons 
            item={item} 
            onLike={onLike} 
            onSave={onSave} 
            onShare={onShare} 
            onComment={onComment}
            formatCount={formatCount}
            t={t}
            vertical
          />
        </div>
      </div>

      {/* Desktop Side Panel */}
      <DesktopSidePanel 
        item={item} 
        onLike={onLike} 
        onSave={onSave} 
        onShare={onShare} 
        onFollow={onFollow}
        onComment={onComment}
        formatCount={formatCount}
        t={t}
        isPoll
      />
    </div>
  );
};

// Shared Components
const CreatorInfo = ({ item, onFollow, t, light = false }) => (
  <Link to={`/profile/${item.username}`} className="flex items-center gap-3 mb-3">
    <div className="p-0.5 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6]">
      <Avatar className={`w-10 h-10 border-2 ${light ? 'border-white/50' : 'border-black'}`}>
        <AvatarImage src={item.profile_picture} />
        <AvatarFallback className="bg-[#1E4ED8] text-white font-bold">
          {item.display_name?.charAt(0) || item.username?.charAt(0)}
        </AvatarFallback>
      </Avatar>
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-white">{item.display_name}</span>
        {item.is_verified && <Sparkles className="w-4 h-4 text-[#3B82F6]" />}
      </div>
      <span className="text-white/70 text-sm">@{item.username}</span>
    </div>
    {!item.is_following && (
      <Button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFollow(item.user_id); }}
        size="sm"
        className="bg-white/20 hover:bg-white/30 text-white rounded-full px-4 backdrop-blur-sm"
      >
        {t('common.follow', 'Suivre')}
      </Button>
    )}
  </Link>
);

const HashtagList = ({ hashtags }) => {
  if (!hashtags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {hashtags.slice(0, 4).map((tag, i) => (
        <span key={i} className="text-white/80 text-xs">#{tag}</span>
      ))}
    </div>
  );
};

const ActionButtons = ({ item, onLike, onSave, onShare, onComment, formatCount, t, vertical = false }) => {
  const containerClass = vertical 
    ? "flex flex-col items-center gap-4" 
    : "flex items-center gap-6";
  
  return (
    <div className={containerClass}>
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => onLike(item.id)}
        className="flex flex-col items-center"
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          item.is_liked ? 'bg-[#D9381E]' : 'bg-black/50 backdrop-blur-sm'
        }`}>
          <Heart className={`w-6 h-6 text-white ${item.is_liked ? 'fill-white' : ''}`} />
        </div>
        <span className="text-white text-xs font-medium mt-1">{formatCount(item.likes_count)}</span>
      </motion.button>

      <button onClick={() => onComment(item.id)} className="flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <span className="text-white text-xs font-medium mt-1">{formatCount(item.comments_count)}</span>
      </button>

      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => onSave(item.id)}
        className="flex flex-col items-center"
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          item.is_saved ? 'bg-[#1E4ED8]' : 'bg-black/50 backdrop-blur-sm'
        }`}>
          <Bookmark className={`w-6 h-6 text-white ${item.is_saved ? 'fill-white' : ''}`} />
        </div>
        <span className="text-white text-xs font-medium mt-1">{t('feed.save', 'Sauv.')}</span>
      </motion.button>

      <button onClick={() => onShare(item.id)} className="flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <Share2 className="w-6 h-6 text-white" />
        </div>
        <span className="text-white text-xs font-medium mt-1">{t('feed.share', 'Partager')}</span>
      </button>
    </div>
  );
};

const DesktopSidePanel = ({ item, onLike, onSave, onShare, onFollow, onComment, formatCount, t, isPoll = false }) => (
  <div className="hidden lg:flex w-96 bg-white flex-col border-l border-gray-100">
    {/* Creator Info */}
    <div className="p-5 border-b border-gray-100">
      <div className="flex items-center justify-between">
        <Link to={`/profile/${item.username}`} className="flex items-center gap-3 group flex-1">
          <div className="relative">
            <div className="p-0.5 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6]">
              <Avatar className="w-14 h-14 border-2 border-white">
                <AvatarImage src={item.profile_picture} />
                <AvatarFallback className="bg-[#1E4ED8]/10 text-[#1E4ED8] font-bold text-lg">
                  {item.display_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{item.display_name}</p>
            <p className="text-sm text-gray-500">@{item.username}</p>
          </div>
        </Link>
        
        {!item.is_following ? (
          <Button onClick={() => onFollow(item.user_id)} size="sm" className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 text-white rounded-full px-5">
            <Plus className="w-4 h-4 mr-1" />
            {t('common.follow', 'Suivre')}
          </Button>
        ) : (
          <Badge className="bg-[#1E4ED8]/10 text-[#1E4ED8]">
            <Users className="w-3.5 h-3.5 mr-1" />
            {t('common.following', 'Abonné')}
          </Badge>
        )}
      </div>

      {/* BARKA */}
      <div className="mt-4 flex items-center gap-3">
        <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#D9381E]/10 to-[#E07A5F]/10 rounded-full px-4 py-1.5">
          <Flame className="w-4 h-4 text-[#D9381E]" />
          <span className="font-bold text-[#D9381E]">{item.barka}</span>
          <span className="text-xs text-[#D9381E]/70">BARKA</span>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-gray-500 text-sm">
          <Eye className="w-4 h-4" />
          {formatCount(item.views_count)}
        </div>
      </div>
    </div>

    {/* Content */}
    <div className="flex-1 overflow-y-auto p-5">
      {item.item_type === 'text_post' && (
        <p className="text-gray-700 leading-relaxed">{item.text_content}</p>
      )}
      {item.item_type === 'video' && (
        <p className="text-gray-700 leading-relaxed">{item.caption}</p>
      )}
      {isPoll && (
        <div className="bg-[#1E4ED8]/5 rounded-2xl p-4">
          <h3 className="font-semibold text-gray-900 mb-2">{item.poll_question}</h3>
          <p className="text-sm text-gray-500">{item.poll_total_votes} votes</p>
        </div>
      )}

      {item.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {item.hashtags.map((tag, i) => (
            <Badge key={i} className="bg-[#1E4ED8]/10 text-[#1E4ED8]">#{tag}</Badge>
          ))}
        </div>
      )}

      {/* Recipe Link */}
      {item.recipe_id && (
        <Link
          to={`/recipe/${item.recipe_id}`}
          className="mt-5 flex items-center gap-3 p-4 bg-gradient-to-r from-[#1E4ED8]/5 to-[#3B82F6]/5 rounded-2xl border border-[#1E4ED8]/20 hover:border-[#1E4ED8]/40 transition-colors"
        >
          {item.recipe_image ? (
            <img src={item.recipe_image} alt="" className="w-12 h-12 rounded-xl object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[#1E4ED8] flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900">{item.recipe_title || t('feed.seeFullRecipe', 'Voir la recette')}</p>
            <p className="text-sm text-gray-500">{t('feed.ingredientsAndSteps', 'Ingrédients et étapes')}</p>
          </div>
        </Link>
      )}
    </div>

    {/* Actions */}
    <div className="p-5 border-t border-gray-100 bg-[#F8F5F2]">
      <div className="flex items-center justify-between">
        <motion.button whileTap={{ scale: 0.9 }} onClick={() => onLike(item.id)} className="flex flex-col items-center gap-1">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
            item.is_liked ? 'bg-[#D9381E] text-white' : 'bg-white text-gray-500 border border-gray-200'
          }`}>
            <Heart className={`w-7 h-7 ${item.is_liked ? 'fill-current' : ''}`} />
          </div>
          <span className="text-xs font-semibold">{formatCount(item.likes_count)}</span>
        </motion.button>

        <button onClick={() => onComment(item.id)} className="flex flex-col items-center gap-1">
          <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-500">
            <MessageCircle className="w-7 h-7" />
          </div>
          <span className="text-xs font-semibold">{formatCount(item.comments_count)}</span>
        </button>

        <motion.button whileTap={{ scale: 0.9 }} onClick={() => onSave(item.id)} className="flex flex-col items-center gap-1">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
            item.is_saved ? 'bg-[#1E4ED8] text-white' : 'bg-white text-gray-500 border border-gray-200'
          }`}>
            <Bookmark className={`w-7 h-7 ${item.is_saved ? 'fill-current' : ''}`} />
          </div>
          <span className="text-xs font-semibold">{t('feed.save', 'Sauv.')}</span>
        </motion.button>

        <button onClick={() => onShare(item.id)} className="flex flex-col items-center gap-1">
          <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-500">
            <Share2 className="w-7 h-7" />
          </div>
          <span className="text-xs font-semibold">{t('feed.share', 'Partager')}</span>
        </button>
      </div>
    </div>
  </div>
);

// Comments Sheet
const CommentsSheet = ({ item, isOpen, onClose, api, user, t }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && item) {
      fetchComments();
    }
  }, [isOpen, item]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/feed/${item.id}/comments`);
      setComments(res.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const res = await api.post(`/feed/${item.id}/comment`, {
        content: newComment
      });
      setComments([res.data, ...comments]);
      setNewComment('');
    } catch (error) {
      toast.error(t('errors.generic', 'Erreur'));
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      className="fixed inset-x-0 bottom-0 h-[70vh] bg-white rounded-t-3xl z-50 flex flex-col shadow-2xl"
    >
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-lg">{item?.comments_count || 0} commentaires</h3>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading', 'Chargement...')}</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">{t('feed.firstComment', 'Soyez le premier à commenter!')}</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 bg-gray-50 rounded-2xl p-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={comment.profile_picture} />
                <AvatarFallback>{comment.username?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <span className="font-semibold text-sm">{comment.display_name}</span>
                <p className="text-sm text-gray-700">{comment.content}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t flex gap-3">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t('feed.addComment', 'Ajouter un commentaire...')}
          className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm"
        />
        <Button type="submit" size="icon" className="rounded-full bg-[#1E4ED8]">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </motion.div>
  );
};

// Main Component
export default function UnifiedFeedPage() {
  const { api, user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [feedType, setFeedType] = useState('fyp');
  const [showComments, setShowComments] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchItems = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      const res = await api.get(`/feed/unified?feed_type=${feedType}&limit=20&offset=${currentOffset}`);
      const newItems = res.data.items || [];
      
      if (reset) {
        setItems(newItems);
        setOffset(20);
      } else {
        setItems(prev => [...prev, ...newItems]);
        setOffset(prev => prev + 20);
      }
      
      setHasMore(res.data.has_more);
    } catch (error) {
      console.error('Failed to fetch feed:', error);
      // Fallback to old video feed if unified doesn't exist yet
      try {
        const res = await api.get(`/videos/fyp?limit=20&offset=${reset ? 0 : offset}`);
        const videos = (res.data.videos || []).map(v => ({ ...v, item_type: 'video' }));
        if (reset) {
          setItems(videos);
        } else {
          setItems(prev => [...prev, ...videos]);
        }
      } catch (e) {
        console.error('Fallback failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [api, feedType, offset]);

  useEffect(() => {
    setLoading(true);
    setActiveIndex(0);
    fetchItems(true);
  }, [feedType]);

  // Lock body scroll while this fullscreen page is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Navigation handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startY = 0;

    const goNext = () => {
      if (activeIndex < items.length - 1) {
        setActiveIndex(prev => prev + 1);
        if (activeIndex >= items.length - 3 && hasMore) {
          fetchItems();
        }
      }
    };

    const goPrev = () => {
      if (activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      }
    };

    const handleTouchStart = (e) => { startY = e.touches[0].clientY; };
    const handleTouchEnd = (e) => {
      const diff = startY - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goPrev();
      }
    };
    const handleWheel = (e) => { e.deltaY > 0 ? goNext() : goPrev(); };
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') goNext();
      else if (e.key === 'ArrowUp') goPrev();
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('wheel', handleWheel);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, items.length, hasMore, fetchItems]);

  // Actions
  const handleLike = async (itemId) => {
    try {
      const res = await api.post(`/feed/${itemId}/like`);
      setItems(prev => prev.map(i => 
        i.id === itemId ? { ...i, is_liked: res.data.liked, likes_count: res.data.likes_count } : i
      ));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSave = async (itemId) => {
    try {
      const res = await api.post(`/feed/${itemId}/save`);
      setItems(prev => prev.map(i => 
        i.id === itemId ? { ...i, is_saved: res.data.saved } : i
      ));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleShare = async (itemId) => {
    const item = items.find(i => i.id === itemId);
    const shareUrl = `${window.location.origin}/feed/${itemId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'MHARHER', url: shareUrl });
        return;
      } catch (err) {}
    }
    
    await navigator.clipboard.writeText(shareUrl);
    toast.success(t('share.copied', 'Lien copié!'));
  };

  const handleFollow = async (userId) => {
    try {
      await api.post(`/users/${userId}/follow`);
      setItems(prev => prev.map(i => 
        i.user_id === userId ? { ...i, is_following: true } : i
      ));
      toast.success(t('success.subscribed'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleComment = (itemId) => {
    setSelectedItem(items.find(i => i.id === itemId));
    setShowComments(true);
  };

  const handleVote = async (pollId, optionId) => {
    const res = await api.post(`/feed/poll/${pollId}/vote`, { option_id: optionId });
    setItems(prev => prev.map(i => 
      i.id === pollId ? { ...i, ...res.data.poll, user_voted_option: optionId } : i
    ));
  };

  if (!isAuthenticated) {
    navigate('/auth');
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative h-[calc(100vh-3.5rem)] lg:h-screen overflow-hidden bg-black"
      data-testid="unified-feed-page"
    >
      {/* Feed Type Toggle */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
        <div className="flex w-max bg-black/50 backdrop-blur-xl rounded-full p-1">
          <button
            onClick={() => setFeedType('following')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              feedType === 'following' ? 'bg-white text-gray-900' : 'text-white/70 hover:text-white'
            }`}
          >
            {t('feed.following', 'Abonnements')}
          </button>
          <button
            onClick={() => setFeedType('fyp')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${
              feedType === 'fyp' ? 'bg-white text-gray-900' : 'text-white/70 hover:text-white'
            }`}
          >
            <Flame className="w-4 h-4" />
            {t('feed.forYou', 'Pour toi')}
          </button>
          <button
            onClick={() => navigate('/live')}
            className="lg:hidden px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap text-white/70 hover:text-white"
          >
            <Radio className="w-4 h-4" />
            {t('nav.live', 'Live')}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 rounded-full border-4 border-white/20 border-t-white"
          />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-24 h-24 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
            <ChefHat className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('feed.noContent', 'Aucun contenu')}</h2>
          <p className="text-white/60 max-w-xs mb-6">{t('feed.beFirst', 'Sois le premier à partager!')}</p>
          <Button onClick={() => navigate('/create')} className="bg-[#1E4ED8] hover:bg-[#1E4ED8]/90 rounded-full">
            <Plus className="w-5 h-5 mr-2" />
            {t('feed.create', 'Créer')}
          </Button>
        </div>
      ) : (
        <div 
          className="h-full transition-transform duration-500 ease-out"
          style={{ transform: `translateY(-${activeIndex * 100}%)` }}
        >
          {items.map((item, index) => (
            <div key={item.id} className="h-full w-full" style={{ height: 'calc(100vh - 3.5rem)' }}>
              {item.item_type === 'video' && (
                <VideoCard
                  item={item}
                  isActive={index === activeIndex}
                  onLike={handleLike}
                  onSave={handleSave}
                  onShare={handleShare}
                  onFollow={handleFollow}
                  onComment={handleComment}
                  isMuted={isMuted}
                  onToggleMute={() => setIsMuted(!isMuted)}
                  t={t}
                />
              )}
              {item.item_type === 'text_post' && (
                <TextPostCard
                  item={item}
                  onLike={handleLike}
                  onSave={handleSave}
                  onShare={handleShare}
                  onFollow={handleFollow}
                  onComment={handleComment}
                  t={t}
                />
              )}
              {item.item_type === 'poll' && (
                <PollCard
                  item={item}
                  onLike={handleLike}
                  onSave={handleSave}
                  onShare={handleShare}
                  onFollow={handleFollow}
                  onComment={handleComment}
                  onVote={handleVote}
                  t={t}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Navigation Hints */}
      <div className="hidden lg:flex absolute bottom-6 left-6 flex-col gap-2 z-20">
        <button 
          onClick={() => activeIndex > 0 && setActiveIndex(prev => prev - 1)}
          disabled={activeIndex === 0}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            activeIndex > 0 ? 'bg-white/20 text-white' : 'bg-white/5 text-white/30'
          }`}
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button 
          onClick={() => activeIndex < items.length - 1 && setActiveIndex(prev => prev + 1)}
          disabled={activeIndex === items.length - 1}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            activeIndex < items.length - 1 ? 'bg-white/20 text-white' : 'bg-white/5 text-white/30'
          }`}
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* Comments Sheet */}
      <AnimatePresence>
        {showComments && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowComments(false)}
            />
            <CommentsSheet
              item={selectedItem}
              isOpen={showComments}
              onClose={() => setShowComments(false)}
              api={api}
              user={user}
              t={t}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
